import { publicConfigSchema } from '@hugo/core'
import { useQuery } from '@tanstack/react-query'
import { FlaskConicalIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { apiFetch } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import { onNativeSignIn, startNativeSignIn } from '@/lib/native-auth'
import { isNative, nativeDebug } from '@/lib/platform'

const ERROR_TEXT: Record<string, string> = {
  email_not_allowed: 'This Google account is not allowed to use hugo.',
  access_denied: 'Sign-in was cancelled.',
  state_mismatch: 'Sign-in expired. Please try again.',
  sign_in_unavailable: 'Google sign-in is not configured on this server.',
  exchange_failed: 'Sign-in could not be completed. Please try again.',
  no_session: 'Sign-in could not be completed. Please try again.',
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  )
}

export function LoginPage({ error: initialError }: { error?: string | undefined }) {
  const [error, setError] = useState<string | undefined>(initialError)
  const [pending, setPending] = useState<'google' | 'test' | null>(null)
  const config = useQuery({
    queryKey: ['public-config'],
    queryFn: () => apiFetch('/api/public/config', publicConfigSchema),
  })

  // On success the session provider loads the new session; this page only shows failures.
  useEffect(
    () =>
      onNativeSignIn((result) => {
        setPending(null)
        if (!result.ok) setError(result.error)
      }),
    [],
  )

  const signInWithGoogle = async () => {
    setError(undefined)
    setPending('google')
    try {
      if (isNative) {
        await startNativeSignIn('google')
        return
      }
      const result = await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/',
        errorCallbackURL: '/login',
      })
      if (result.error) {
        setError(result.error.code?.toLowerCase() ?? 'sign_in_unavailable')
        setPending(null)
      }
    } catch {
      setError('sign_in_unavailable')
      setPending(null)
    }
  }

  const signInAsTestUser = async () => {
    setError(undefined)
    setPending('test')
    if (isNative) {
      await startNativeSignIn('debug')
      return
    }
    window.location.assign('/api/e2e/login?scenario=empty&name=Test%20User&redirect=/')
  }

  const state = config.isPending ? 'loading' : config.isError ? 'error' : 'ready'
  const showTestLogin = isNative ? nativeDebug : config.data?.testMode === true

  return (
    <main
      data-view="login"
      data-state={state}
      className="flex min-h-svh items-center justify-center bg-muted/40 px-4 pt-(--safe-top) pb-(--safe-bottom)"
    >
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img src="/favicon.svg" alt="" className="mx-auto mb-2 size-12 rounded-xl" />
          <CardTitle>
            <h1 className="font-heading text-2xl font-semibold">Sign in to hugo</h1>
          </CardTitle>
          <CardDescription>
            Everything you owe, everything you are waiting on, in one place.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {error ? (
            <Alert variant="destructive" data-testid="login-error">
              <AlertTitle>Could not sign in</AlertTitle>
              <AlertDescription>{ERROR_TEXT[error] ?? 'Something went wrong.'}</AlertDescription>
            </Alert>
          ) : null}
          {config.isError ? (
            <Alert variant="destructive">
              <AlertTitle>hugo is unreachable</AlertTitle>
              <AlertDescription>Check your connection and try again.</AlertDescription>
            </Alert>
          ) : null}
          <Button
            size="lg"
            variant="outline"
            onClick={() => void signInWithGoogle()}
            disabled={pending !== null || (config.isSuccess && !config.data.google)}
          >
            {pending === 'google' ? <Spinner /> : <GoogleIcon />}
            Continue with Google
          </Button>
          {config.isSuccess && !config.data.google ? (
            <p className="text-center text-xs text-muted-foreground">
              Google sign-in is not configured on this server.
            </p>
          ) : null}
          {showTestLogin ? (
            <Button
              size="lg"
              variant="secondary"
              onClick={() => void signInAsTestUser()}
              disabled={pending !== null}
            >
              {pending === 'test' ? <Spinner /> : <FlaskConicalIcon aria-hidden="true" />}
              Sign in as test user
            </Button>
          ) : null}
        </CardContent>
        <CardFooter className="justify-center text-xs text-muted-foreground">
          {config.data ? `Version ${config.data.version}` : '\u00a0'}
        </CardFooter>
      </Card>
    </main>
  )
}
